import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  PlusCircle, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  TrendingUp,
  ArrowRight,
  ArrowLeft,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t, isRTL } = useLanguage();
  const { isAdmin, isAdminOrInventory } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const receiptsRes = await axios.get(`${API}/receipts`);
      setRecentReceipts(receiptsRes.data.slice(0, 5));
      
      if (isAdminOrInventory()) {
        const statsRes = await axios.get(`${API}/stats`);
        setStats(statsRes.data);
      } else {
        // Calculate basic stats for non-admin
        const received = receiptsRes.data.filter(r => r.receipt_status === 'received').length;
        const notReceived = receiptsRes.data.filter(r => r.receipt_status === 'not_received').length;
        setStats({
          total_received: received,
          total_not_received: notReceived,
          total_records: receiptsRes.data.length
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          {t('dashboard')}
        </h1>
        <p className="text-slate-500">{t('intro_text')}</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in stagger-1">
        <Link to="/new-receipt">
          <Card className="group cursor-pointer border-2 border-dashed border-sky-200 bg-sky-50/50 hover:border-sky-400 hover:bg-sky-50 transition-colors duration-200">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                <PlusCircle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{t('new_receipt')}</h3>
                <p className="text-sm text-slate-500">
                  {isRTL ? 'تسجيل مريض جديد' : 'Register new patient'}
                </p>
              </div>
              <Arrow className="h-5 w-5 text-sky-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/receipts">
          <Card className="group cursor-pointer border-2 border-dashed border-emerald-200 bg-emerald-50/50 hover:border-emerald-400 hover:bg-emerald-50 transition-colors duration-200">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{t('receipts_list')}</h3>
                <p className="text-sm text-slate-500">
                  {isRTL ? 'عرض جميع سجلات الاستلام' : 'View all receipt records'}
                </p>
              </div>
              <Arrow className="h-5 w-5 text-emerald-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in stagger-2">
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t('total_records')}</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_records}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t('total_received')}</p>
                  <p className="text-3xl font-bold text-emerald-600">{stats.total_received}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t('total_not_received')}</p>
                  <p className="text-3xl font-bold text-red-600">{stats.total_not_received}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Records */}
      <Card className="animate-fade-in stagger-3">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t('recent_records')}</CardTitle>
          <Link to="/receipts">
            <Button variant="ghost" size="sm" className="gap-1">
              {isRTL ? 'عرض الكل' : 'View All'}
              <Arrow className="h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentReceipts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {t('no_data')}
            </div>
          ) : (
            <div className="space-y-3">
              {recentReceipts.map((receipt, index) => (
                <div 
                  key={receipt.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors duration-150"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-600">
                        {receipt.patient_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{receipt.patient_name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="h-3 w-3" />
                        <span>{receipt.receipt_date}</span>
                        <span>•</span>
                        <span>{receipt.hospital_name}</span>
                      </div>
                    </div>
                  </div>
                  <Badge 
                    className={
                      receipt.receipt_status === 'received' 
                        ? 'bg-emerald-500 hover:bg-emerald-600' 
                        : receipt.receipt_status === 'preparing'
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-red-500 hover:bg-red-600'
                    }
                  >
                    {receipt.receipt_status === 'received' 
                      ? t('received') 
                      : receipt.receipt_status === 'preparing'
                      ? t('preparing')
                      : t('not_received')
                    }
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
